// Descarga del Anexo RDEP (SRI) y de la planilla SUT (IESS). Nómina.
// Lo que importa aquí no es el botón: es que la validación se ve ANTES.
// El DIMM rechaza el anexo completo por una sola cédula mal digitada y no
// dice cuál; esta pantalla la nombra.
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Download, FileWarning } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, nomRpc, type AnexoArchivo, type AnexoValidacion, type PagoFormato } from './shared';

function descargar(nombre: string, contenido: string) {
  const blob = new Blob(['﻿' + contenido], { type: 'text/plain;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = nombre;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function AnexoDescarga({ tipo, anio, mes, titulo }:
  { tipo: 'rdep' | 'sut'; anio: number; mes?: number; titulo: string }) {
  const [formatoId, setFormatoId] = useState('');
  const [forzar, setForzar] = useState(false);

  const { data: formatos = [] } = useQuery<PagoFormato[]>({
    queryKey: ['nom-pago-formatos', tipo],
    queryFn: () => nomRpc<PagoFormato[]>('nom_pago_formatos', { _uso: tipo }),
  });
  const { data: val } = useQuery<AnexoValidacion>({
    queryKey: ['nom-anexo-validar', tipo, anio, mes ?? null],
    queryFn: () => nomRpc<AnexoValidacion>('nom_anexo_validar', { _tipo: tipo, _anio: anio, _mes: mes ?? null }),
  });

  const bajar = useMutation({
    mutationFn: () => nomRpc<AnexoArchivo>('nom_anexo_archivo', {
      _tipo: tipo, _anio: anio, _mes: mes ?? null, _formato_id: formatoId || null, _forzar: forzar,
    }),
    onSuccess: (a) => {
      descargar(a.archivo, a.contenido);
      toast.success(`${a.archivo}: ${a.lineas} línea(s)`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const graves = val?.graves ?? 0;

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">{titulo}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {val && (
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span><b className="tabular-nums">{val.personas}</b> persona(s)</span>
            {graves === 0 && val.avisos === 0 && (
              <span className="inline-flex items-center gap-1 text-ok">
                <CheckCircle2 className="w-4 h-4" /> Sin problemas: listo para subir
              </span>
            )}
            {graves > 0 && (
              <span className="inline-flex items-center gap-1 text-destructive">
                <AlertTriangle className="w-4 h-4" /> {graves} problema(s) que el organismo rechazaría
              </span>
            )}
            {val.avisos > 0 && (
              <span className="inline-flex items-center gap-1 text-pendiente">
                <FileWarning className="w-4 h-4" /> {val.avisos} aviso(s)
              </span>
            )}
          </div>
        )}

        {val && val.problemas.length > 0 && (
          <div className="rounded-md border border-border bg-muted/30 p-3 max-h-44 overflow-y-auto text-xs space-y-1">
            {val.problemas.map((p, i) => (
              <div key={i} className="flex gap-2 border-b border-border/40 pb-1 last:border-0">
                <span className={p.grave ? 'text-destructive font-semibold shrink-0' : 'text-pendiente shrink-0'}>
                  {p.grave ? 'Grave' : 'Aviso'}
                </span>
                <span className="font-medium">{p.quien}</span>
                <span className="text-muted-foreground">{p.problema}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <select className={`${fieldCls} w-72`} value={formatoId} onChange={(e) => setFormatoId(e.target.value)}
            aria-label="Formato del anexo">
            {formatos.filter((f) => f.activo).map((f) => (
              <option key={f.id} value={f.id}>{f.nombre}</option>
            ))}
          </select>
          {graves > 0 && (
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={forzar} onChange={(e) => setForzar(e.target.checked)} />
              Generarlo igual, con los {graves} problema(s)
            </label>
          )}
          <Button variant="outline" isLoading={bajar.isPending}
            disabled={!val || val.personas === 0 || (graves > 0 && !forzar)}
            onClick={() => bajar.mutate()}>
            <Download className="w-4 h-4 mr-1" /> Descargar archivo
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          El sistema calcula los datos; el <b>formato</b> del archivo se edita en Pagos → «Formatos del banco»
          (los layouts que vienen de fábrica son un punto de partida: contrástalos con el DIMM o el SUT del año).
        </p>
      </CardContent>
    </Card>
  );
}
